/**
 * ImageUpload.jsx
 * ===============
 * Drag-and-drop / file-picker for uploading drone or satellite images.
 * Sends the file to Flask API /api/predict and displays the result.
 */

import React, { useState, useRef, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const CROP_COLORS = {
  Paddy:     '#00FF00',
  Sugarcane: '#FFD700',
  Banana:    '#FF6B35',
  Coconut:   '#228B22',
  Groundnut: '#DEB887',
  Fallow:    '#8B4513',
};
const STAGE_COLORS = {
  Early:        '#FFFF00',
  Intermediate: '#90EE90',
  Mature:       '#006400',
  Harvested:    '#A0522D',
};

export default function ImageUpload({ onResult }) {
  const [dragOver, setDragOver]   = useState(false);
  const [loading, setLoading]     = useState(false);
  const [result, setResult]       = useState(null);
  const [error, setError]         = useState(null);
  const [preview, setPreview]     = useState(null);
  const [progress, setProgress]   = useState(0);
  const fileRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/tiff', 'image/tif'];
    if (!allowed.includes(file.type) && !file.name.endsWith('.tif') && !file.name.endsWith('.tiff')) {
      setError('Please upload a PNG, JPG, or TIFF image.');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      setError('File too large (max 50 MB).');
      return;
    }

    // Show preview
    if (file.type.startsWith('image/')) {
      const url = URL.createObjectURL(file);
      setPreview(url);
    }

    setError(null);
    setResult(null);
    setLoading(true);
    setProgress(10);

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Simulate progress
      const interval = setInterval(() => {
        setProgress(prev => Math.min(prev + 15, 85));
      }, 300);

      const resp = await fetch(`${API_BASE}/api/predict`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(interval);
      setProgress(100);

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: resp.statusText }));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }

      const data = await resp.json();
      setResult(data);
      onResult && onResult(data);

    } catch (err) {
      // Demo mode fallback — simulate result when backend is down
      if (err.message.includes('fetch') || err.message.includes('Failed')) {
        const demoResult = simulateDemoResult(file.name);
        setResult(demoResult);
        onResult && onResult(demoResult);
        setError('⚠ Backend offline — showing demo prediction');
      } else {
        setError(`Error: ${err.message}`);
      }
    } finally {
      setLoading(false);
      setTimeout(() => setProgress(0), 800);
    }
  }, [onResult]);

  // ── Drag & Drop handlers ───────────────────────────────────────────────────
  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  }, [handleFile]);

  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFile(file);
    e.target.value = '';
  };

  const cropColor  = CROP_COLORS[result?.crop_type]     || '#94a3b8';
  const stageColor = STAGE_COLORS[result?.growth_stage] || '#94a3b8';

  return (
    <div className="space-y-3">
      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">
        📷 Image Classification
      </div>

      {/* ── Drop Zone ────────────────────────────────────────────────────── */}
      <div
        className={`relative rounded-lg border-2 border-dashed p-4 text-center cursor-pointer transition-all ${
          dragOver
            ? 'border-green-400 bg-green-900/20'
            : 'border-slate-600 hover:border-slate-400 bg-slate-900/30'
        }`}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => fileRef.current?.click()}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".png,.jpg,.jpeg,.tif,.tiff"
          className="hidden"
          onChange={onFileChange}
        />

        {preview ? (
          <div className="relative">
            <img
              src={preview}
              alt="preview"
              className="w-full h-28 object-cover rounded-md"
            />
            <div className="absolute inset-0 flex items-center justify-center
                            bg-black/30 rounded-md opacity-0 hover:opacity-100 transition-opacity">
              <span className="text-white text-xs font-medium">Change image</span>
            </div>
          </div>
        ) : (
          <>
            <div className="text-3xl mb-2">{dragOver ? '📥' : '🛰️'}</div>
            <div className="text-sm text-slate-300 font-medium">
              {dragOver ? 'Drop image here' : 'Drag & drop satellite image'}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              PNG · JPG · TIFF (max 50 MB)
            </div>
            <div className="mt-2">
              <span className="text-xs bg-slate-700 hover:bg-slate-600 text-slate-300
                               px-3 py-1 rounded-full transition-colors">
                Browse File
              </span>
            </div>
          </>
        )}
      </div>

      {/* ── Progress bar ─────────────────────────────────────────────────── */}
      {loading && (
        <div>
          <div className="flex justify-between text-xs text-slate-400 mb-1">
            <span>Analysing image …</span>
            <span>{progress}%</span>
          </div>
          <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="text-xs text-slate-500 mt-1 text-center animate-pulse">
            Running MobileNetV2 inference …
          </div>
        </div>
      )}

      {/* ── Error message ─────────────────────────────────────────────────── */}
      {error && (
        <div className="bg-red-900/30 border border-red-700/50 rounded-lg px-3 py-2 text-xs text-red-300">
          {error}
        </div>
      )}

      {/* ── Prediction result ─────────────────────────────────────────────── */}
      {result && !loading && (
        <div
          className="rounded-lg p-3 border fade-in"
          style={{ borderColor: cropColor + '60', background: cropColor + '10' }}
        >
          <div className="text-xs font-bold text-slate-300 mb-2 flex items-center gap-1">
            🔬 Classification Result
          </div>

          {/* Main result */}
          <div className="flex items-center gap-3 mb-3 pb-2 border-b border-slate-700/50">
            <div
              className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center
                         text-2xl border-2"
              style={{ borderColor: cropColor, background: cropColor + '20' }}
            >
              {CROP_ICONS[result.crop_type] || '🌱'}
            </div>
            <div>
              <div className="font-bold text-lg" style={{ color: cropColor }}>
                {result.crop_type}
              </div>
              <div className="text-xs text-slate-400">
                {result.growth_stage} Stage
              </div>
            </div>
            <div className="ml-auto text-right">
              <div
                className="text-lg font-bold"
                style={{ color: result.confidence >= 0.8 ? '#4ade80' : '#fbbf24' }}
              >
                {Math.round(result.confidence * 100)}%
              </div>
              <div className="text-xs text-slate-400">confidence</div>
            </div>
          </div>

          {/* Detail rows */}
          <div className="space-y-2 text-xs">
            <ResultDetail
              label="Growth Stage"
              value={result.growth_stage}
              color={stageColor}
              confidence={result.stage_confidence}
            />
            <ResultDetail
              label="NDVI Value"
              value={result.ndvi_value?.toFixed(4) ?? 'N/A'}
              color={result.ndvi_value > 0.5 ? '#4ade80' : '#fbbf24'}
            />
          </div>

          {/* All crop probabilities */}
          {result.all_crop_probs && (
            <div className="mt-3 pt-2 border-t border-slate-700/50">
              <div className="text-xs text-slate-400 mb-1.5">All Probabilities:</div>
              {Object.entries(result.all_crop_probs)
                .sort((a, b) => b[1] - a[1])
                .map(([crop, prob]) => (
                  <div key={crop} className="flex items-center gap-2 mb-1">
                    <div
                      className="w-2 h-2 rounded-sm flex-shrink-0"
                      style={{ background: CROP_COLORS[crop] || '#94a3b8' }}
                    />
                    <span className="text-slate-400 w-20">{crop}</span>
                    <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(prob * 100).toFixed(1)}%`,
                          background: CROP_COLORS[crop] || '#94a3b8',
                        }}
                      />
                    </div>
                    <span className="text-slate-500 w-10 text-right">
                      {(prob * 100).toFixed(1)}%
                    </span>
                  </div>
                ))}
            </div>
          )}

          {/* Classify another button */}
          <button
            onClick={() => { setResult(null); setPreview(null); setError(null); }}
            className="mt-3 w-full text-xs bg-slate-700 hover:bg-slate-600 text-slate-300
                       py-1.5 rounded transition-colors"
          >
            Classify Another Image
          </button>
        </div>
      )}

      {/* ── API status ───────────────────────────────────────────────────── */}
      <ApiStatus />
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ResultDetail({ label, value, color, confidence }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-slate-400">{label}</span>
      <div className="flex items-center gap-2">
        <span style={{ color }} className="font-medium">{value}</span>
        {confidence != null && (
          <span className="text-slate-500">
            ({Math.round(confidence * 100)}%)
          </span>
        )}
      </div>
    </div>
  );
}

function ApiStatus() {
  const [status, setStatus] = React.useState('checking');

  React.useEffect(() => {
    fetch(`${API_BASE}/api/health`, { signal: AbortSignal.timeout(3000) })
      .then(r => r.json())
      .then(d => setStatus(d.status === 'ok' ? 'online' : 'error'))
      .catch(() => setStatus('offline'));
  }, []);

  const colors = { online: '#4ade80', offline: '#f87171', checking: '#fbbf24', error: '#f87171' };
  const labels = { online: 'API Online', offline: 'Connecting…', checking: 'Connecting…', error: 'API Offline' };

  return (
    <div className="flex items-center gap-1.5 text-xs text-slate-500">
      <div className="w-1.5 h-1.5 rounded-full" style={{ background: colors[status] }} />
      {labels[status]}
    </div>
  );
}

// ─── Demo fallback ────────────────────────────────────────────────────────────
function simulateDemoResult(filename) {
  const crops  = ['Paddy', 'Sugarcane', 'Banana', 'Coconut', 'Groundnut', 'Fallow'];
  const stages = ['Early', 'Intermediate', 'Mature', 'Harvested'];
  let h = 0;
  for (let i = 0; i < filename.length; i++) h = (h * 31 + filename.charCodeAt(i)) >>> 0;
  const crop  = crops[h  % crops.length];
  const stage = stages[(h >> 3) % stages.length];
  const conf  = 0.65 + (h % 25) / 100;
  const ndvi  = +(0.15 + (h % 70) / 100).toFixed(4);
  return {
    crop_type: crop, confidence: +conf.toFixed(4),
    growth_stage: stage, stage_confidence: +(conf - 0.05).toFixed(4),
    ndvi_value: ndvi, demo_mode: true,
  };
}

const CROP_ICONS = {
  Paddy: '🌾', Sugarcane: '🎋', Banana: '🍌',
  Coconut: '🥥', Groundnut: '🥜', Fallow: '🟫',
};
